import { useState } from 'react';
import { DocumentArrowUpIcon, TrashIcon, UserIcon } from '@heroicons/react/24/outline';
import { StrapiFile } from 'types/StrapiFile';
import { Constants } from 'constants/Constants';
import Button from 'common/components/Button/Button';
import { Dots } from 'react-activity';

interface DocumentItemProps {
  document: StrapiFile & { type?: 'applicant' | 'mediator' };
  applicantName?: string;
  onDelete?: (documentId: number) => Promise<void>;
  canDelete?: boolean;
}

/**
 * Composant pour afficher un document individuel avec options de visualisation, téléchargement et suppression
 */
const DocumentItem = ({
  document,
  applicantName,
  onDelete,
  canDelete = false,
}: DocumentItemProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleView = () => {
    window.open(`${Constants.ROOT_URL}${document.url}`, '_blank');
  };

  const handleDownload = async () => {
    try {
      setIsProcessing(true);

      // Récupérer le fichier via fetch pour forcer le téléchargement
      const response = await fetch(`${Constants.ROOT_URL}${document.url}`);
      const blob = await response.blob();

      // Créer une URL pour le blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Créer un lien temporaire pour télécharger le fichier
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.name || 'document';
      link.style.display = 'none';

      // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
      window.document.body.appendChild(link);
      link.click();

      // Nettoyer
      setTimeout(() => {
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        setIsProcessing(false);
      }, 100);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      setIsProcessing(true);
      await onDelete(document.id);
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-row items-center gap-2 mt-2 w-full">
      <DocumentArrowUpIcon className="w-8 h-8 flex-shrink-0" />

      <div
        className="flex flex-row items-center gap-2 p-3 bg-slate-200 rounded-xl cursor-pointer hover:bg-slate-300 transition-colors flex-1"
        onClick={handleView}
      >
        <div className="flex-1" onClick={handleView}>
          <p className="font-medium">{document.alternativeText ?? document.name}</p>
          <div className="flex flex-row gap-1 items-center">
            <UserIcon className="w-3 h-3 flex-shrink-0 mt-0.5 ml-1" />
            <p className="flex-1 text-sm">
              {document.type === 'applicant' ? applicantName : 'Moi'}
            </p>
          </div>
        </div>
        {isProcessing ? (
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
            <Dots />
          </div>
        ) : (
          <div className="flex flex-col gap-1 w-8 h-8">
            <Button
              type="neutral-line"
              icon="AiOutlineDownload"
              small
              compact
              onClick={handleDownload}
            />
            {canDelete && document.type === 'mediator' && (
              <Button
                type="error-line"
                icon="TrashIcon"
                small
                compact
                onClick={handleDelete}
                title="Supprimer"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentItem;
